import { useEffect, useMemo, useRef, useState } from "react";

import Alert from "components/atoms/alert/Alert";
import useLoading from "components/atoms/loading/useLoading";
import useToast from "components/atoms/toast/useToast";

import {
    legacyTextToMessageDoc,
    messageDocHasPrepMention,
    messageDocToPlainText,
    prependMentionToMessageDoc,
    stringifyMessageDoc,
    updateMentionCandidates,
} from "components/atoms/message";
import { getSpaceMemberProfiles } from "utils/request/space";
import { wsUrl } from "utils/urls";
import { uuidv4 } from "utils/uuid";

export function useReferenceReviewWebSocket({
    reference_uuid,
    user,
    connectionDependencies = [],
    onFileUploaded,
    onFileDeleted,
    onCreateReviewBox,
    onPdfMetadata,
    onStreamStart,
}) {
    const { setToast } = useToast();

    // 로딩 상태
    const { setLoading } = useLoading();

    // 공통 상태
    const wsRef = useRef(null);
    const connectWebSocketRef = useRef(null);
    const [threads, setThreads] = useState([]);
    const [isThreadListLoading, setIsThreadListLoading] = useState(false);
    const [selectedThread, setSelectedThread] = useState(null);
    const [profiles, setProfiles] = useState([]);
    const [streamingMessageIds, setStreamingMessageIds] = useState([]);
    const [autoScrollToEnd, setAutoScrollToEnd] = useState(true);
    const [pdfIndexingInfo, setPdfIndexingInfo] = useState({
        total_pages: 1,
        indexed_pages: 0,
        analysis_status: "TODO",
    });
    const [pdfMetadataStatus, setPdfMetadataStatus] = useState("IDLE");
    const pendingMessageRef = useRef(null);
    const [myProfileId, setMyProfileId] = useState(null);
    const [spaceUuid, setSpaceUuid] = useState(null);
    const [isPaid, setIsPaid] = useState(false);

    // region: 프로필 정보 관리 (공통)
    useEffect(() => {
        if (!spaceUuid) {
            return;
        }
        getSpaceMemberProfiles(spaceUuid)
            .then((res) => {
                setProfiles(res);
                updateMentionCandidates(res);
            })
            .catch((err) => {
                console.log("profiles error", err);
            });
    }, [spaceUuid]);

    // region: 웹소켓 연결 로직 (connect/disconnect)
    const connectWebSocket = () => {
        if (wsRef.current !== null) return; // 이미 연결되어 있으면 무시
        wsRef.current = new WebSocket(wsUrl("referenceReview"));
        wsRef.current.senderId = uuidv4();

        wsRef.current.onopen = () => {
            if (wsRef.current) {
                sendMessage(
                    {
                        action: "init",
                        reference_uuid: reference_uuid,
                    },
                    handleMessage,
                );
            }
        };

        // 메시지를 보낼 때 다시 연결하기 때문에 콘솔 로그만 남김
        wsRef.current.onerror = (error) => {
            console.log(error);
        };

        wsRef.current.onclose = (event) => {
            if (event.code !== 1000) {
                // 정상 종료가 아닌 경우
                console.log(event);
            }
            setIsThreadListLoading(false);
            wsRef.current = null;
        };
    };
    connectWebSocketRef.current = connectWebSocket;

    const connectionReady = useMemo(() => connectionDependencies.every(Boolean), [connectionDependencies]);
    useEffect(() => {
        setThreads([]);
        setIsThreadListLoading(connectionReady);
        setSelectedThread(null);
        setStreamingMessageIds([]);
        setAutoScrollToEnd(true);
        setPdfIndexingInfo({
            total_pages: 1,
            indexed_pages: 0,
            analysis_status: "TODO",
        });
        setPdfMetadataStatus("IDLE");
        pendingMessageRef.current = null;

        if (connectionReady) {
            connectWebSocketRef.current();
        }
        return () => {
            // 웹소켓 연결 종료
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
    }, [connectionReady, reference_uuid]);

    useEffect(() => {
        setSelectedThread((prevSelectedThread) => {
            if (!prevSelectedThread?.id) return prevSelectedThread;

            const nextSelectedThread = threads.find((thread) => thread.id === prevSelectedThread.id);
            if (!nextSelectedThread || nextSelectedThread.is_deleted) return null;
            if (nextSelectedThread === prevSelectedThread) return prevSelectedThread;
            return nextSelectedThread;
        });
    }, [threads]);

    // region: 메시지 송신
    const sendMessage = (message_obj, message_handler) => {
        // console.log("sendMessage", message_obj);

        if (wsRef.current) {
            wsRef.current.onmessage = message_handler;
            wsRef.current.send(
                JSON.stringify({
                    ...message_obj,
                    sender_id: wsRef.current.senderId,
                }),
            );
        } else {
            pendingMessageRef.current = message_obj;
            connectWebSocketRef.current();
            return;
        }
    };

    const chatStart = (req_message) => {
        let has_prep_mention = messageDocHasPrepMention(req_message.content);

        // 유료 기능 체크
        if (!isPaid) {
            if (req_message.autoResponse) {
                Alert(
                    "info",
                    "유료 기능 알림",
                    "AI 응답은 유료 기능입니다.\n대화창 하단의 AI 자동 응답 기능을 끄고, 메모를 남길 수 있습니다.",
                );
                return;
            }

            if (has_prep_mention) {
                Alert("info", "유료 기능 알림", "AI 응답은 유료 기능입니다.\n@PREP 멘션을 사용할 수 없습니다.");
                return;
            }

            if (req_message.editMessageId) {
                Alert("info", "유료 기능 알림", "메시지 재작성은 유료 기능입니다.");
                return;
            }
        }

        if (req_message.autoResponse && !has_prep_mention) {
            // AI 자동 응답 선택 시 @PREP 멘션 자동 추가
            req_message.content = prependMentionToMessageDoc(req_message.content, { id: 0, display: "PREP" });
        }

        setLoading(true);
        setAutoScrollToEnd(true);
        sendMessage(
            {
                action: "chat_start",
                thread_id: selectedThread ? selectedThread.id : null,
                parent_message_id: req_message.parentMessageId,
                review_selection_id: req_message.reviewSelectionId,
                review_selection_color: req_message.reviewSelectionColor,
                content: req_message.content,
                new_review_selection_data: req_message.newReviewSelectionData
                    ? { ...req_message.newReviewSelectionData }
                    : null,
                edit_message_id: req_message.editMessageId,
            },
            handleMessage,
        );
    };

    const chatStop = () => {
        setLoading(true);
        sendMessage(
            {
                action: "chat_stop",
            },
            handleMessage,
        );
    };

    const chatDelete = (thread_id, message_id, is_restore = false) => {
        setLoading(true);
        sendMessage(
            {
                action: "chat_delete",
                thread_id: thread_id,
                message_id: message_id,
                is_restore: is_restore,
            },
            handleMessage,
        );
    };

    const threadUpdateTitle = (thread_id, title) => {
        setLoading(true);
        sendMessage(
            {
                action: "thread_update_title",
                thread_id: thread_id,
                title: title,
            },
            handleMessage,
        );
    };

    // region: 메시지 수신 핸들러
    const handleMessage = async (event) => {
        setLoading(false);

        const data = JSON.parse(event.data);
        const action = data?.action;
        const payload = data?.payload;
        const sender_id = data?.sender_id;
        const is_my_message = sender_id === wsRef.current.senderId;

        if (!(data && action && payload)) {
            return;
        }
        setAutoScrollToEnd(is_my_message);

        if (action === "init_success") {
            const threads_data = payload.threads_data;
            if (is_my_message) {
                setSpaceUuid(payload.space_uuid);

                setIsPaid(payload.is_paid || false);

                setMyProfileId(payload.space_member_id);

                setThreads((prevThreads) => {
                    return [...threads_data];
                });
                setIsThreadListLoading(false);

                if (pendingMessageRef.current) {
                    // 연결이 끊긴 상태에서 메시지를 보내면 대기 상태로 저장 후 연결되면 전송
                    const message_obj = pendingMessageRef.current;
                    if (message_obj.action === "chat_start") {
                        // 유료 처리가 필요한 chat_start 와 다른 메시지를 구분하여 처리
                        chatStart(message_obj);
                    } else {
                        sendMessage(
                            {
                                ...message_obj,
                                sender_id: wsRef.current.senderId,
                            },
                            handleMessage,
                        );
                    }
                    pendingMessageRef.current = null;
                }
            }
        }

        if (action === "update_thread") {
            const thread_data = payload.thread_data;
            updateThreads(thread_data);
            if (is_my_message) {
                setSelectedThread(thread_data);
            }
        }

        if (action === "update_thread_title") {
            const thread_data = payload.thread_data;
            updateThreads(thread_data);
            if (is_my_message) {
                setToast("채팅 제목이 수정되었습니다.", "info");
            }
        }

        if (action === "delete_thread") {
            const thread_data = payload.thread_data;
            updateThreads(thread_data);
            if (is_my_message) {
                setToast("스레드를 삭제했습니다.", "info");
            }
        }

        if (action === "update_message") {
            const message_data = payload.message_data;
            updateMessage(message_data);
        }

        if (action === "delete_message") {
            const message_data = payload.message_data;
            updateMessage(message_data);
            if (is_my_message) {
                if (message_data.is_deleted) {
                    setToast("메시지를 삭제했습니다.", "info");
                } else {
                    setToast("메시지를 복원했습니다.", "info");
                }
            }
        }

        if (action === "update_message_content") {
            updateMessageContent(payload.thread_id, payload.message_id, payload.content);
        }

        if (action === "start_stream") {
            updateMessageContent(payload.thread_id, payload.message_id, "");
            if (is_my_message && onStreamStart) {
                onStreamStart(payload.message_id);
            }
        }

        if (action === "stop_stream") {
            setStreamingMessageIds([]);
        }

        if (action === "create_review_selection") {
            if (onCreateReviewBox) {
                onCreateReviewBox(payload.review_selection_data);
            }
        }

        if (action === "pdf_indexing") {
            setPdfIndexingInfo(payload);
        }

        if (action === "pdf_uploaded") {
            setToast("PDF 파일이 업로드되었습니다.", "info");
            if (onFileUploaded) {
                onFileUploaded();
            }
        }

        if (action === "pdf_deleted") {
            setToast("PDF 파일이 삭제되었습니다.", "info");
            if (onFileDeleted) {
                onFileDeleted();
            }
        }

        if (action === "pdf_metadata") {
            setPdfMetadataStatus("IDLE");
            setToast("PDF 파일에서 서지정보를 추출했습니다.", "info");
            if (onPdfMetadata) {
                onPdfMetadata();
            }
        }

        if (action === "pdf_metadata_started") {
            setPdfMetadataStatus("RUNNING");
        }

        if (action === "pdf_metadata_failed") {
            setPdfMetadataStatus("IDLE");
            setToast("PDF 파일에서 서지정보를 추출하지 못했습니다.", "error");
        }
    };

    const updateThreads = (thread_data) => {
        setThreads((prevThreads) => {
            const cur_idx = prevThreads.findIndex((x) => x.id === thread_data.id);
            if (cur_idx < 0) {
                // 새로운 스레드 생성
                return [thread_data, ...prevThreads];
            } else {
                // 기존 스레드 변경
                return prevThreads.map((thread, idx) => (idx === cur_idx ? thread_data : thread));
            }
        });

        // 스트리밍 메시지 UI 초기화
        setStreamingMessageIds([]);
    };

    const updateMessage = (message_data) => {
        setThreads((prevThreads) => {
            const target_thread = prevThreads.find((x) => x.id === message_data.thread);
            if (!target_thread) return prevThreads;
            const target_message = target_thread.messages.find((x) => x.id === message_data.id);
            if (target_message) {
                target_message.is_deleted = message_data.is_deleted;
            }

            if (selectedThread && selectedThread.id === message_data.thread) {
                setSelectedThread({ ...target_thread });
            }

            return [...prevThreads];
        });
    };

    const updateMessageContent = (thread_id, message_id, content) => {
        setThreads((prevThreads) => {
            const target_thread = prevThreads.find((x) => x.id === thread_id);
            if (!target_thread) return prevThreads;
            const target_message = target_thread.messages.find((x) => x.id === message_id);
            if (target_message) {
                const previousText = content ? messageDocToPlainText(target_message.content) : "";
                target_message.content = stringifyMessageDoc(legacyTextToMessageDoc(`${previousText}${content || ""}`));
            }

            if (selectedThread && selectedThread.id === thread_id) {
                setSelectedThread({ ...target_thread });
            }

            setStreamingMessageIds((prev) => (prev.includes(message_id) ? prev : [...prev, message_id]));

            return [...prevThreads];
        });
    };

    // region: 객체 반환
    return {
        threads,
        isThreadListLoading,
        selectedThread,
        setSelectedThread,
        profiles,
        streamingMessageIds,
        autoScrollToEnd,
        setAutoScrollToEnd,
        pdfIndexingInfo,
        pdfMetadataStatus,
        chatStart,
        chatStop,
        chatDelete,
        threadUpdateTitle,
        myProfileId,
    };
}
